/**
 * @fileoverview CloseX: the app's one close X (B111, owner decisions Q7 and Q9). B107's look, taken from the tray photo's remove
 * X: a solid card-coloured circle with an edge and a shadow, 32px to see and 44px to hit. The dialog, the sheet, the share studio
 * and the tray photo all draw it, so the look is changed in this one file. A forwardRef button, so Radix's Close can wear it
 * with asChild.
 *
 * Where: components/ui/dialog, components/ui/sheet, features/share/studio/ShareStepBands, features/photo-tray/components/PhotoStrip.
 * Words (en/es): sitewide.close.
 *
 * @module CloseX
 */
import * as React from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** CloseX's props: a button's, and the name a screen reader hears. */
export interface CloseXProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The accessible name; defaults to the sitewide "Close". */
  label?: string;
}

/**
 * The close X. A host places it with className (its class comes after the look) and passes any button prop through.
 *
 * @param props - CloseXProps.
 */
export const CloseX = React.forwardRef<HTMLButtonElement, CloseXProps>(({ label, className, ...props }, ref) => {
  const { reactTranslate: t } = useTranslation();
  const name = label ?? t('sitewide.close');
  return (
    <button
      type="button"
      aria-label={name}
      title={name}
      ref={ref}
      {...props}
      className={cn(
        "relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-md before:absolute before:-inset-1.5 before:rounded-full before:content-[''] hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50",
        className,
      )}
    >
      <X className="h-5 w-5" aria-hidden="true" />
    </button>
  );
});
CloseX.displayName = 'CloseX';
